import ClearRoundedIcon from '@material-ui/icons/ClearRounded';
import EditOutlinedIcon from '@material-ui/icons/EditOutlined';
import SaveAltRoundedIcon from '@material-ui/icons/SaveAltRounded';
import React from 'react';
import styled, { css } from 'styled-components/macro';
import { iconButtonStyles } from '../../common/elements/Button';
import {
  focusRing,
  fontSize,
  fontWeight,
  layout,
  lineHeight,
  neutral,
  radius,
  shadow,
  space,
  transition,
} from '../../../theme/tokens';

export const StaffWhiteboard = styled.section`
  grid-area: staffWhiteboard;
  background: ${neutral.surface};
  box-shadow: ${shadow.md};
  border-radius: 0 0 ${radius.lg} ${radius.lg};
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: ${space[1]};
  z-index: 10;
  padding: ${space[3]} ${space[4]};
  position: relative;
  ${({ isIndividual }) =>
    isIndividual &&
    css`
      align-items: center;
    `}
`;

export const ClassroomTitle = styled.h2`
  margin: 0;
  /* keep clear of the absolutely positioned close button */
  padding-right: ${layout.touchTarget};
  font-weight: ${fontWeight.bold};
  font-size: ${fontSize.lg};
  line-height: ${lineHeight.tight};
  color: ${({ theme }) => theme.headerColour};
  overflow-wrap: anywhere;
`;

export const ClassroomSubHeading = styled.h3`
  margin: 0;
  color: ${({ theme }) => theme.subHeaderColour};
  font-size: ${fontSize.xs};
  font-weight: ${fontWeight.semibold};
  line-height: ${lineHeight.tight};
`;

export const WhiteboardContentForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${space[1]};
  margin: ${space[2]} 0 0;
  padding: ${space[1]} ${space[3]} ${space[2]};
  border: 1px solid ${neutral.border};
  border-radius: ${radius.md};
  background: ${neutral.canvas};
  transition: background ${transition.fast}, border-color ${transition.fast};

  ${({ $editing }) =>
    $editing &&
    css`
      background: ${neutral.surface};
      border-color: ${neutral.borderStrong};
    `}
`;

export const WhiteboardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${space[2]};
`;

export const WhiteboardLabel = styled.p`
  margin: 0;
  font-size: ${fontSize.xs};
  font-weight: ${fontWeight.bold};
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: ${neutral.textMuted};
`;

export const Inline = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${space[1]};
`;

export const WhiteboardInput = styled.textarea`
  height: 100%;
  width: 100%;
  background: none;
  border: none;
  display: block;
  resize: none;
  padding: 0;
  font-family: inherit;
  font-size: ${fontSize.md};
  line-height: ${lineHeight.normal};
  color: ${({ theme }) => theme.headerColour};
  overflow-wrap: anywhere;
  &:disabled {
    color: ${({ theme }) => theme.headerColour};
  }
  &::placeholder {
    color: ${neutral.textMuted};
    opacity: 1;
  }
  &:focus {
    outline: none;
  }
`;

export const Controls = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: center;
  gap: ${space[2]};
  margin-top: ${space[1]};
`;

export const IconButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: ${space[1]};
  min-height: ${layout.touchTarget};
  margin-left: -${space[2]};
  padding: 0 ${space[2]};
  border: none;
  border-radius: ${radius.sm};
  background: none;
  font-family: inherit;
  font-size: ${fontSize.xs};
  font-weight: ${fontWeight.bold};
  color: ${({ theme }) => theme.headerColour};
  cursor: pointer;
  transition: background ${transition.fast};
  &:hover {
    background: ${({ theme }) => theme.hoverFill};
  }
  ${focusRing}
`;

// sits at the right of the whiteboard header, so it bleeds right rather than left
export const WhiteboardAction = styled(IconButton)`
  min-height: 3.6rem;
  margin-left: 0;
  &:last-child {
    margin-right: -${space[2]};
  }
`;

export const EditIcon = (props) => {
  return <EditOutlinedIcon {...props} style={{ fontSize: '1.6rem' }} />;
};
export const SaveIcon = (props) => {
  return <SaveAltRoundedIcon {...props} style={{ fontSize: '1.6rem' }} />;
};
export const ClearIcon = (props) => {
  return <ClearRoundedIcon {...props} style={{ fontSize: '1.6rem' }} />;
};

export const CloseButton = styled.button`
  ${iconButtonStyles}
  position: absolute;
  top: ${space[1]};
  right: ${space[1]};
`;

export * from '../../common/elements/Form';
export * from '../../common/elements/Staff';
export * from './UserCard';
